/* ==========================================================================
   Behind The Broadcast — Shopify commerce behaviour
   Cart drawer + AJAX cart, quick add, predictive search, product carousels,
   collection filters, announcement rotation.
   ========================================================================== */
(function () {
  'use strict';

  const R = () => window.BTB.routes;
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------------------------------------------------------------- CART */

  const Cart = {
    drawer: null,
    lastFocus: null,

    init() {
      this.drawer = $('#CartDrawer');

      document.addEventListener('click', (e) => {
        if (e.target.closest('.js-cart-close')) {
          e.preventDefault();
          this.close();
        }
        const toggle = e.target.closest('.js-cart-toggle');
        if (toggle && window.BTB.cartType === 'drawer') {
          e.preventDefault();
          this.open();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });

      document.addEventListener('btb:cart:updated', (e) => {
        this.render(e.detail && e.detail.sections);
        this.refreshCount();
        if (e.detail && e.detail.open) this.open();
      });

      // Quantity changes & removal, from the drawer or the cart page
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('.js-cart-qty');
        if (!btn) return;
        e.preventDefault();
        this.change(btn.dataset.key, parseInt(btn.dataset.qty, 10));
      });

      document.addEventListener('change', (e) => {
        const input = e.target.closest('.js-cart-qty-input');
        if (!input) return;
        this.change(input.dataset.key, parseInt(input.value, 10));
      });

      // Order note
      let noteTimer;
      document.addEventListener('input', (e) => {
        const note = e.target.closest('.js-cart-note');
        if (!note) return;
        clearTimeout(noteTimer);
        noteTimer = setTimeout(() => {
          fetch(R().cartUpdate, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: note.value })
          });
        }, 600);
      });

      // Quick add from product cards
      document.addEventListener('click', async (e) => {
        const btn = e.target.closest('.js-quick-add');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        btn.classList.add('is-loading');
        try {
          await this.add(btn.dataset.variantId, 1);
        } finally {
          btn.classList.remove('is-loading');
        }
      });
    },

    isOpen() {
      return this.drawer && this.drawer.classList.contains('is-open');
    },

    open() {
      if (!this.drawer) {
        window.location.href = R().cart;
        return;
      }
      this.lastFocus = document.activeElement;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
      const close = $('.cart-drawer-close', this.drawer);
      if (close) close.focus();
    },

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
      if (this.lastFocus) this.lastFocus.focus();
    },

    async add(id, quantity) {
      const res = await fetch(R().cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity }], sections: 'cart-drawer' })
      });
      const data = await res.json();
      if (data.status) {
        this.toast(data.description || data.message);
        return;
      }
      document.dispatchEvent(new CustomEvent('btb:cart:updated', {
        detail: { sections: data.sections, open: window.BTB.cartType === 'drawer' }
      }));
    },

    async change(key, quantity) {
      if (Number.isNaN(quantity) || quantity < 0) return;
      const res = await fetch(R().cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: 'cart-drawer' })
      });
      const data = await res.json();
      document.dispatchEvent(new CustomEvent('btb:cart:updated', { detail: { sections: data.sections } }));
      if (document.body.classList.contains('template-cart')) {
        // The cart page renders the same snippet — re-fetch it.
        this.reloadCartPage();
      }
    },

    render(sections) {
      if (!sections || !sections['cart-drawer']) return;
      const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const fresh = html.querySelector('#CartDrawerBodyInner');
      const target = $('#CartDrawerBody');
      if (fresh && target) target.innerHTML = fresh.innerHTML;

      const countEl = html.querySelector('[data-cart-count-value]');
      if (countEl) this.setCount(parseInt(countEl.textContent.trim(), 10));
    },

    async reloadCartPage() {
      const res = await fetch(window.location.pathname, { headers: { Accept: 'text/html' } });
      const html = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = html.querySelector('#CartPageBody');
      const target = $('#CartPageBody');
      if (fresh && target) target.innerHTML = fresh.innerHTML;
    },

    async refreshCount() {
      try {
        const res = await fetch(`${R().root}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        this.setCount(cart.item_count);
      } catch (e) { /* offline — leave the badge alone */ }
    },

    setCount(count) {
      if (typeof count !== 'number' || Number.isNaN(count)) return;
      $$('.js-cart-count').forEach((el) => {
        el.textContent = count;
        el.dataset.cartCount = count;
        el.classList.toggle('is-empty', count === 0);
      });
      $$('.cart-drawer-count').forEach((el) => { el.textContent = `(${count})`; });
    },

    toast(message) {
      if (!message) return;
      let el = $('#CartToast');
      if (!el) {
        el = document.createElement('div');
        el.id = 'CartToast';
        el.className = 'cart-toast';
        el.setAttribute('role', 'status');
        document.body.appendChild(el);
      }
      el.textContent = message;
      el.classList.add('is-visible');
      setTimeout(() => el.classList.remove('is-visible'), 4000);
    }
  };

  /* -------------------------------------------------------------- SEARCH */

  const Search = {
    init() {
      const overlay = $('#SearchOverlay');
      if (!overlay) return;

      document.addEventListener('click', (e) => {
        if (e.target.closest('.js-open-search')) {
          e.preventDefault();
          overlay.hidden = false;
          document.body.classList.add('drawer-open');
          const input = $('#SearchInput');
          if (input) input.focus();
        }
        if (e.target.closest('.js-close-search') || e.target === overlay) {
          overlay.hidden = true;
          document.body.classList.remove('drawer-open');
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !overlay.hidden) {
          overlay.hidden = true;
          document.body.classList.remove('drawer-open');
        }
      });

      const input = $('[data-predictive-search]');
      const results = $('#PredictiveSearchResults');
      if (!input || !results) return;

      let timer;
      let controller;
      input.addEventListener('input', () => {
        clearTimeout(timer);
        const term = input.value.trim();
        if (term.length < 2) {
          results.innerHTML = '';
          return;
        }
        timer = setTimeout(async () => {
          if (controller) controller.abort();
          controller = new AbortController();
          try {
            const url = `${R().predictiveSearch}?q=${encodeURIComponent(term)}&resources[type]=product,collection,page,article&resources[limit]=6&section_id=predictive-search`;
            const res = await fetch(url, { signal: controller.signal });
            const html = new DOMParser().parseFromString(await res.text(), 'text/html');
            const fresh = html.querySelector('#PredictiveSearchInner');
            results.innerHTML = fresh ? fresh.innerHTML : '';
          } catch (e) { /* aborted */ }
        }, 250);
      });
    }
  };

  /* ----------------------------------------------------------- CAROUSELS */

  function initCarousels() {
    $$('.js-product-carousel').forEach((grid) => {
      if (grid.dataset.carouselInit) return;
      grid.dataset.carouselInit = 'true';

      const step = () => {
        const card = grid.querySelector('.product-card, .shop-card');
        return card ? card.offsetWidth + 20 : 340;
      };
      const prev = grid.dataset.prev ? $(grid.dataset.prev) : null;
      const next = grid.dataset.next ? $(grid.dataset.next) : null;
      if (prev) prev.addEventListener('click', (e) => { e.preventDefault(); grid.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', (e) => { e.preventDefault(); grid.scrollBy({ left: step(), behavior: 'smooth' }); });

      // Drag to scroll, without swallowing genuine clicks
      let down = false, startX = 0, startScroll = 0, dragged = false;
      grid.style.cursor = 'grab';
      grid.querySelectorAll('img').forEach((img) => img.addEventListener('dragstart', (e) => e.preventDefault()));
      grid.addEventListener('mousedown', (e) => {
        down = true; dragged = false;
        grid.style.cursor = 'grabbing';
        startX = e.pageX - grid.offsetLeft;
        startScroll = grid.scrollLeft;
      });
      const release = () => { down = false; grid.style.cursor = 'grab'; };
      grid.addEventListener('mouseleave', release);
      grid.addEventListener('mouseup', release);
      grid.addEventListener('mousemove', (e) => {
        if (!down) return;
        const walk = ((e.pageX - grid.offsetLeft) - startX) * 1.5;
        if (Math.abs(walk) > 5) { dragged = true; e.preventDefault(); }
        grid.scrollLeft = startScroll - walk;
      });
      grid.addEventListener('click', (e) => {
        if (e.target.closest('.card-swatch, .card-swatch-count, .js-quick-add')) return;
        if (dragged) { e.preventDefault(); e.stopPropagation(); }
      }, true);
    });

    // YouTube grids
    $$('.js-yt-prev, .js-yt-next').forEach((btn) => {
      if (btn.dataset.init) return;
      btn.dataset.init = 'true';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const grid = $(btn.dataset.target);
        if (!grid) return;
        const card = grid.querySelector('.youtube-card');
        const amount = card ? card.offsetWidth + 20 : 320;
        grid.scrollBy({ left: btn.classList.contains('js-yt-next') ? amount : -amount, behavior: 'smooth' });
      });
    });
  }

  /* -------------------------------------------- LAZY RECOMMENDATIONS ---- */

  function initRecommendations() {
    $$('[data-recommendations]').forEach((section) => {
      if (section.dataset.loaded) return;
      const load = async () => {
        section.dataset.loaded = 'true';
        try {
          const res = await fetch(section.dataset.url);
          const html = new DOMParser().parseFromString(await res.text(), 'text/html');
          const fresh = html.querySelector('.recommendations-section');
          if (fresh && fresh.innerHTML.trim()) {
            section.innerHTML = fresh.innerHTML;
            initCarousels();
            if (window.initBtbTheme) window.initBtbTheme();
          }
        } catch (e) { /* leave the section empty */ }
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) { io.disconnect(); load(); }
          });
        }, { rootMargin: '400px' });
        io.observe(section);
      } else {
        load();
      }
    });
  }

  /* ------------------------------------------------- COLLECTION FILTERS */

  function initCollectionFilters() {
    const form = $('#CollectionFilterForm');
    if (!form || form.dataset.init) return;
    form.dataset.init = 'true';

    let timer;
    const apply = async () => {
      const params = new URLSearchParams(new FormData(form));
      // Drop empty values so the URL stays clean and canonical-friendly
      Array.from(params.keys()).forEach((key) => {
        if (!params.get(key)) params.delete(key);
      });
      const url = `${window.location.pathname}?${params.toString()}`;
      const res = await fetch(url, { headers: { Accept: 'text/html' } });
      const html = new DOMParser().parseFromString(await res.text(), 'text/html');

      ['#shop-grid', '.shop-filters', '.shop-result-count', '.pagination'].forEach((sel) => {
        const fresh = html.querySelector(sel);
        const current = $(sel);
        if (fresh && current) current.replaceWith(fresh);
        else if (!fresh && current) current.innerHTML = '';
      });

      window.history.replaceState({}, '', url);
      form.dataset.init = '';
      initCollectionFilters();
      if (window.initBtbTheme) window.initBtbTheme();
    };

    form.addEventListener('change', () => { clearTimeout(timer); timer = setTimeout(apply, 120); });
    form.addEventListener('submit', (e) => { e.preventDefault(); apply(); });
  }

  /* ------------------------------------------------ ANNOUNCEMENT ROTATE */

  function initAnnouncement() {
    const bar = $('#top-bar[data-rotate]');
    if (!bar || bar.dataset.init) return;
    bar.dataset.init = 'true';
    const messages = $$('.top-bar-message', bar);
    if (messages.length < 2) return;
    let i = 0;
    setInterval(() => {
      messages[i].classList.add('is-hidden');
      i = (i + 1) % messages.length;
      messages[i].classList.remove('is-hidden');
    }, parseInt(bar.dataset.rotate, 10) || 6000);
  }

  /* ------------------------------------------------------------- BOOT -- */

  function boot() {
    initCarousels();
    initRecommendations();
    initCollectionFilters();
    initAnnouncement();
  }

  document.addEventListener('DOMContentLoaded', () => {
    Cart.init();
    Search.init();
    boot();
  });
  document.addEventListener('shopify:section:load', boot);

  window.BTBCart = Cart;
})();
